import React, { useContext, useState, useEffect, useMemo, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Row, Col, Button, Modal, Alert, Spinner, Form, Table, Badge } from "react-bootstrap";
import axios from "axios";
import {
    FaShoppingCart, FaCalendarAlt, FaBox, FaPlus, FaTimes, FaEdit, FaTrash,
    FaSave, FaTimesCircle, FaSync, FaCheck
} from "react-icons/fa";
import moment from "moment";
import Swal from 'sweetalert2';
import Select from "react-select";

export default function PetSaleComponent({ appointmentId, petId, isAppointmentOpen }) {
    const { t } = useContext(TranslatorContext);
    const [sales, setSales] = useState([]);
    const [appointmentNum, setAppointmentNum] = useState('');
    const [loading, setLoading] = useState(true);
    const [initializing, setInitializing] = useState(true);

    // Add Sale States
    const [showAddModal, setShowAddModal] = useState(false);
    const [newSaleData, setNewSaleData] = useState({
        sale_date: moment().format('YYYY-MM-DD'),
        notes: '',
        products: []
    });

    // Edit States
    const [editingRowId, setEditingRowId] = useState(null);
    const [editingData, setEditingData] = useState(null);
    const [availableProducts, setAvailableProducts] = useState([]);
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [updating, setUpdating] = useState(false);

    // Delete Confirmation State
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deletingSale, setDeletingSale] = useState(null);

    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchSales();
    }, [appointmentId]);

    useEffect(() => {
        if (editingRowId) {
            fetchAvailableProducts();
        }
    }, [editingRowId]);

    const userRole = localStorage.getItem("role");
    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const canEditDelete = () => userRole === 'Admin' || userRole === 'admin' ;
    const canAddSale = () => isAppointmentOpen;
    const isSalePaid = (sale) => sale && sale.status === 'paid';

    const showAlert = (type, message) => {
        Swal.fire({
            icon: type,
            title: type === 'success' ? 'Success!' : type === 'warning' ? 'Warning!' : 'Error!',
            text: message,
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true
        });
    };

    const handleApiError = (error, defaultMessage) => {
        console.error("API Error:", error);
        if (error.response) {
            const status = error.response.status;
            const message = error.response.data?.message || error.response.data?.error;
            if (status === 401) showAlert('error', 'Session expired. Please login again.');
            else if (status === 403) showAlert('error', 'You don\'t have permission to perform this action.');
            else if (status === 422) return error.response.data;
            else showAlert('error', message || defaultMessage);
        } else if (error.request) {
            showAlert('error', 'Network error. Please check your connection.');
        } else {
            showAlert('error', defaultMessage);
        }
        return null;
    };

    const fetchSales = async () => {
        try {
            setLoading(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/customer-sale/${appointmentId}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data && response.data.status) {
                if (response.data.sales && Array.isArray(response.data.sales)) {
                    setSales(response.data.sales);
                    setAppointmentNum(response.data.appointment_number || '');
                } else {
                    setSales([]);
                }
            } else {
                setSales([]);
            }
        } catch (error) {
            console.error("Error fetching sales:", error);
            handleApiError(error, 'Failed to fetch sales');
            setSales([]);
        } finally {
            setLoading(false);
            setInitializing(false);
        }
    };

    const fetchAvailableProducts = async () => {
        try {
            setLoadingProducts(true);
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/getAvailableProducts`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            if (response.data && response.data.status) {
                setAvailableProducts(response.data.products || []);
            } else {
                setAvailableProducts([]);
            }
        } catch (error) {
            console.error("Error fetching products:", error);
            handleApiError(error, 'Failed to fetch products');
            setAvailableProducts([]);
        } finally {
            setLoadingProducts(false);
        }
    };

    const handleRefresh = async () => {
        await fetchSales();
        showAlert('success', 'Data refreshed successfully');
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return moment(dateString).format("DD MMM YYYY");
    };

    const getAvailableStock = (productId) => {
        const product = availableProducts.find(p => p.product_id === productId);
        return product ? product.stock_qty : 0;
    };

    // Function to get selected product IDs (excluding current product being edited)
    const getSelectedProductIds = (currentTempId = null, isEditing = false, currentEditingData = null) => {
        if (isEditing && currentEditingData) {
            return currentEditingData.products
                .filter(p => p.temp_id !== currentTempId && p.product_id)
                .map(p => parseInt(p.product_id));
        } else {
            return newSaleData.products
                .filter(p => p.temp_id !== currentTempId && p.product_id)
                .map(p => parseInt(p.product_id));
        }
    };

    // Get filtered products for dropdown
    const getFilteredProducts = (currentTempId = null, isEditing = false, currentEditingData = null) => {
        const selectedProductIds = getSelectedProductIds(currentTempId, isEditing, currentEditingData);
        return availableProducts.filter(product => !selectedProductIds.includes(product.product_id));
    };

    // Add Sale Functions
    const handleOpenAddModal = async () => {
        await fetchAvailableProducts();
        setNewSaleData({
            sale_date: moment().format('YYYY-MM-DD'),
            notes: '',
            products: []
        });
        setShowAddModal(true);
    };

    const handleAddProductRow = () => {
        setNewSaleData(prev => ({
            ...prev,
            products: [
                ...prev.products,
                {
                    product_id: '',
                    name: '',
                    qty: 1,
                    price: 0,
                    discount_price: 0,
                    extra_price: 0,
                    total_amount: 0,
                    temp_id: Date.now() + Math.random()
                }
            ]
        }));
    };

    const handleRemoveProductRow = (tempId) => {
        setNewSaleData(prev => ({
            ...prev,
            products: prev.products.filter(p => p.temp_id !== tempId)
        }));
    };

    const handleProductChange = (tempId, field, value) => {
        setNewSaleData(prev => ({
            ...prev,
            products: prev.products.map(product => {
                if (product.temp_id === tempId) {
                    const updatedProduct = { ...product, [field]: value };

                    if (field === 'product_id' && value) {
                        const selectedProduct = availableProducts.find(p => p.product_id === parseInt(value));
                        if (selectedProduct) {
                            updatedProduct.name = selectedProduct.product_name;
                            updatedProduct.price = selectedProduct.sale_price || 0;
                        }
                        updatedProduct.qty = 1;
                    }

                    // Recalculate total
                    if (field === 'qty' || field === 'price' || field === 'discount_price' || field === 'extra_price') {
                        const subtotal = (updatedProduct.price || 0) * (updatedProduct.qty || 0);
                        const discount = parseFloat(updatedProduct.discount_price) || 0;
                        const extra = parseFloat(updatedProduct.extra_price) || 0;
                        updatedProduct.total_amount = subtotal - discount + extra;
                    }

                    return updatedProduct;
                }
                return product;
            })
        }));
    };

    const calculateNewGrandTotal = () => {
        return newSaleData.products.reduce((total, product) => {
            return total + (parseFloat(product.total_amount) || 0);
        }, 0);
    };

    const handleCreateSale = async () => {
        if (newSaleData.products.length === 0) {
            showAlert('warning', 'At least one product is required');
            return;
        }

        // Validate products
        for (const product of newSaleData.products) {
            if (!product.product_id) {
                showAlert('warning', 'Please select a product for all rows');
                return;
            }
            if (!product.qty || product.qty < 1) {
                showAlert('warning', 'Quantity must be at least 1');
                return;
            }

            // Check stock
            const availableStock = getAvailableStock(product.product_id);
            if (product.qty > availableStock) {
                showAlert('warning', `${product.name}: Only ${availableStock} items available in stock`);
                return;
            }
        }

        setSubmitting(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const submitData = {
                appointment_id: appointmentId,
                sale_date: newSaleData.sale_date,
                notes: newSaleData.notes,
                products: newSaleData.products.map(product => ({
                    product_id: parseInt(product.product_id),
                    quantity: parseInt(product.qty),
                    sale_price: parseFloat(product.price) || 0,
                    discount_price: parseFloat(product.discount_price) || 0,
                    extra_price: parseFloat(product.extra_price) || 0
                }))
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/customer-sale`,
                submitData,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data && response.data.status) {
                setShowAddModal(false);
                setNewSaleData({
                    sale_date: moment().format('YYYY-MM-DD'),
                    notes: '',
                    products: []
                });
                await fetchSales();
                showAlert('success', 'Sale created successfully');
            } else {
                showAlert('error', response.data.message || 'Failed to create sale');
            }
        } catch (error) {
            console.error("Error creating sale:", error);
            handleApiError(error, 'An error occurred while creating the sale');
        } finally {
            setSubmitting(false);
        }
    };

    // Edit Functions
    const handleEditClick = (sale) => {
        if (isSalePaid(sale) && !canEditDelete()) {
            showAlert('warning', 'Cannot edit a paid sale. Only Admin can edit paid sales.');
            return;
        }
        if (!canEditDelete()) {
            showAlert('error', 'Only Admin can edit sales');
            return;
        }

        setEditingRowId(sale.id);
        setEditingData({
            id: sale.id,
            sale_date: moment(sale.sale_date).format('YYYY-MM-DD'),
            notes: sale.notes || '',
            products: (sale.sale_summary || []).map(item => ({
                product_id: item.product_id,
                name: item.product_name,
                qty: item.quantity,
                price: item.sub_total / item.quantity,
                discount_price: item.discount_price || 0,
                extra_price: item.extra_price || 0,
                total_amount: item.total_amount,
                temp_id: Date.now() + Math.random()
            }))
        });
    };

    const handleCancelEdit = () => {
        setEditingRowId(null);
        setEditingData(null);
    };

    const handleEditProductChange = (tempId, field, value) => {
        setEditingData(prev => ({
            ...prev,
            products: prev.products.map(product => {
                if (product.temp_id === tempId) {
                    const updatedProduct = { ...product, [field]: value };

                    if (field === 'product_id' && value) {
                        const selectedProduct = availableProducts.find(p => p.product_id === parseInt(value));
                        if (selectedProduct) {
                            updatedProduct.name = selectedProduct.product_name;
                            updatedProduct.price = selectedProduct.sale_price || 0;
                        }
                        updatedProduct.qty = 1;
                    }

                    if (field === 'qty' || field === 'price' || field === 'discount_price' || field === 'extra_price') {
                        const subtotal = (updatedProduct.price || 0) * (updatedProduct.qty || 0);
                        const discount = parseFloat(updatedProduct.discount_price) || 0;
                        const extra = parseFloat(updatedProduct.extra_price) || 0;
                        updatedProduct.total_amount = subtotal - discount + extra;
                    }

                    return updatedProduct;
                }
                return product;
            })
        }));
    };

    const handleEditAddProduct = () => {
        setEditingData(prev => ({
            ...prev,
            products: [
                ...prev.products,
                {
                    product_id: '',
                    name: '',
                    qty: 1,
                    price: 0,
                    discount_price: 0,
                    extra_price: 0,
                    total_amount: 0,
                    temp_id: Date.now() + Math.random()
                }
            ]
        }));
    };

    const handleEditRemoveProduct = (tempId) => {
        setEditingData(prev => ({
            ...prev,
            products: prev.products.filter(p => p.temp_id !== tempId)
        }));
    };

    const calculateEditGrandTotal = () => {
        if (!editingData) return 0;
        return editingData.products.reduce((total, product) => {
            return total + (parseFloat(product.total_amount) || 0);
        }, 0);
    };

    const handleUpdateSale = async () => {
        if (!editingData || editingData.products.length === 0) {
            showAlert('warning', 'At least one product is required');
            return;
        }

        for (const product of editingData.products) {
            if (!product.product_id) {
                showAlert('warning', 'Please select a product for all rows');
                return;
            }
            if (!product.qty || product.qty < 1) {
                showAlert('warning', 'Quantity must be at least 1');
                return;
            }

            const availableStock = getAvailableStock(product.product_id);
            if (product.qty > availableStock) {
                showAlert('warning', `${product.name}: Only ${availableStock} items available in stock`);
                return;
            }
        }

        setUpdating(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const submitData = {
                sale_date: editingData.sale_date,
                notes: editingData.notes,
                products: editingData.products.map(product => ({
                    product_id: parseInt(product.product_id),
                    quantity: parseInt(product.qty),
                    sale_price: parseFloat(product.price) || 0,
                    discount_price: parseFloat(product.discount_price) || 0,
                    extra_price: parseFloat(product.extra_price) || 0
                }))
            };

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/customer-sale-update/${editingData.id}`,
                submitData,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );

            if (response.data && response.data.status) {
                setEditingRowId(null);
                setEditingData(null);
                await fetchSales();
                showAlert('success', 'Sale updated successfully');
            } else {
                showAlert('error', response.data.message || 'Failed to update sale');
            }
        } catch (error) {
            console.error("Error updating sale:", error);
            handleApiError(error, 'An error occurred while updating the sale');
        } finally {
            setUpdating(false);
        }
    };

    const handleDeleteClick = (sale) => {
        if (isSalePaid(sale) && !canEditDelete()) {
            showAlert('warning', 'Cannot delete a paid sale. Only Admin can delete paid sales.');
            return;
        }
        if (sale.payments && sale.payments.length > 0 && !canEditDelete()) {
            showAlert('warning', 'Cannot delete sale with existing payments');
            return;
        }
        setDeletingSale(sale);
        setShowDeleteConfirm(true);
    };

    const handleConfirmDelete = async () => {
        if (!deletingSale) return;
        setSubmitting(true);
        try {
            const AuthToken = localStorage.getItem("authToken");
            const response = await axios.delete(
                `${process.env.REACT_APP_API_URL}/customer-sale-delete/${deletingSale.id}`,
                { headers: { Authorization: `Bearer ${AuthToken}` } }
            );
            if (response.data && response.data.status) {
                setShowDeleteConfirm(false);
                setDeletingSale(null);
                await fetchSales();
                showAlert('success', 'Sale deleted successfully');
            } else {
                showAlert('error', response.data.message || 'Failed to delete sale');
            }
        } catch (error) {
            console.error("Error deleting sale:", error);
            handleApiError(error, 'An error occurred while deleting the sale');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading || initializing) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3 mb-0">{t('loading sales') || 'Loading sales...'}</p>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4 pb-2 border-bottom">
                <div>
                    <h6 className="mb-0 text-muted">{t('sales list') || 'Sales List'}</h6>
                    {appointmentNum && <small className="text-muted">Appointment: {appointmentNum}</small>}
                </div>
                <div className="d-flex gap-2">
                    {canAddSale() && sales.length === 0 && (
                        <Button variant="primary" size="sm" onClick={handleOpenAddModal}>
                            <FaPlus size={12} /> {t('add sale') || 'Add Sale'}
                        </Button>
                    )}
                    <Button variant="secondary" size="sm" onClick={handleRefresh} disabled={loading}>
                        <FaSync className={loading ? 'fa-spin' : ''} /> {t('Refresh') || 'Refresh'}
                    </Button>
                </div>
            </div>

            {sales.length === 0 ? (
                <Alert variant="info" className="text-center py-4">
                    <FaShoppingCart size={30} className="mb-2 text-muted" />
                    <p className="mb-0">{t('no sales found') || 'No sales found'}</p>
                </Alert>
            ) : (
                <div>
                    {sales.map((sale) => (
                        <div key={sale.id} className="mb-4 border rounded">
                            {editingRowId === sale.id && editingData ? (
                                // Edit Mode
                                <div className="p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="mb-0">Editing: {sale.sale_number}</h6>
                                        <div>
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={handleCancelEdit}
                                                className="me-2"
                                                disabled={updating}
                                            >
                                                <FaTimes /> Cancel
                                            </Button>
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={handleUpdateSale}
                                                disabled={updating}
                                            >
                                                {updating ? <Spinner animation="border" size="sm" /> : <FaCheck />} Save
                                            </Button>
                                        </div>
                                    </div>

                                    <Form>
                                        <Row className="mb-3">
                                            <Col md={12}>
                                                <Form.Group>
                                                    <Form.Label>Sale Date</Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        value={editingData.sale_date}
                                                        onChange={(e) => setEditingData({ ...editingData, sale_date: e.target.value })}
                                                    />
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        <div className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <Form.Label className="mb-0">Products</Form.Label>
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={handleEditAddProduct}
                                                    disabled={loadingProducts}
                                                >
                                                    <FaPlus size={12} /> Add Product
                                                </Button>
                                            </div>

                                            <div className="table-responsive">
                                                <Table bordered hover size="sm">
                                                    <thead className="table-light">
                                                        <tr>
                                                            <th>Product</th>
                                                            <th style={{ width: '120px' }}>Quantity</th>
                                                            <th style={{ width: '120px' }}>Price</th>
                                                            <th style={{ width: '100px' }}>Discount</th>
                                                            <th style={{ width: '100px' }}>Extra</th>
                                                            <th style={{ width: '100px' }}>Total</th>
                                                            <th style={{ width: '50px' }}></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {editingData.products.map((product) => {
                                                            const availableStock = product.product_id ? getAvailableStock(product.product_id) : 0;
                                                            const filteredProducts = getFilteredProducts(product.temp_id, true, editingData);
                                                            
                                                            return (
                                                                <tr key={product.temp_id}>
                                                                    <td>
                                                                        <Select
                                                                            options={filteredProducts.map(prod => ({
                                                                                value: prod.product_id,
                                                                                label: `${prod.product_name} (Stock: ${prod.stock_qty})`
                                                                            }))}
                                                                            value={product.product_id ? {
                                                                                value: product.product_id,
                                                                                label: product.name
                                                                            } : null}
                                                                            onChange={(option) => handleEditProductChange(product.temp_id, 'product_id', option?.value)}
                                                                            placeholder="Select product"
                                                                            classNamePrefix="react-select"
                                                                            isClearable
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="number"
                                                                            min="1"
                                                                            max={availableStock}
                                                                            placeholder="Enter Qty"
                                                                            value={product.qty || ''}
                                                                            onChange={(e) => handleEditProductChange(product.temp_id, 'qty', parseInt(e.target.value) || 0)}
                                                                            isInvalid={product.qty > availableStock}
                                                                        />
                                                                        <small className="text-muted d-block mt-1">
                                                                            Stock: {availableStock}
                                                                        </small>
                                                                        {product.product_id != null &&
                                                                            product.product_id !== '' &&
                                                                            product.qty > availableStock && (
                                                                                <small className="text-danger">
                                                                                    Exceeds available stock!
                                                                                </small>
                                                                            )}
                                                                    </td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            placeholder="Enter price"
                                                                            value={product.price || ''}
                                                                            onChange={(e) => handleEditProductChange(product.temp_id, 'price', parseFloat(e.target.value) || 0)}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            placeholder="Enter Discount"
                                                                            value={product.discount_price || ''}
                                                                            onChange={(e) => handleEditProductChange(product.temp_id, 'discount_price', parseFloat(e.target.value) || 0)}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="number"
                                                                            step="0.01"
                                                                            min="0"
                                                                            placeholder="Enter Extra"
                                                                            value={product.extra_price || ''}
                                                                            onChange={(e) => handleEditProductChange(product.temp_id, 'extra_price', parseFloat(e.target.value) || 0)}
                                                                        />
                                                                    </td>
                                                                    <td className="fw-bold">
                                                                        {product.total_amount || '0.00'}
                                                                    </td>
                                                                    <td className="text-center">
                                                                        <Button
                                                                            variant="link"
                                                                            className="text-danger p-0"
                                                                            onClick={() => handleEditRemoveProduct(product.temp_id)}
                                                                        >
                                                                            <FaTimes />
                                                                        </Button>
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                    <tfoot>
                                                        <tr className="table-active">
                                                            <td colSpan="5" className="text-end fw-bold">Grand Total:</td>
                                                            <td className="fw-bold text-primary">
                                                                {calculateEditGrandTotal()}
                                                            </td>
                                                            <td></td>
                                                        </tr>
                                                    </tfoot>
                                                </Table>
                                            </div>
                                        </div>

                                        <Form.Group>
                                            <Form.Label>Notes</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={editingData.notes}
                                                onChange={(e) => setEditingData({ ...editingData, notes: e.target.value })}
                                                placeholder="Additional notes..."
                                            />
                                        </Form.Group>
                                    </Form>
                                </div>
                            ) : (
                                // View Mode
                                <div>
                                    <div className="bg-light p-3 border-bottom d-flex justify-content-between align-items-center">
                                        <div>
                                            <strong>{sale.sale_number}</strong>
                                            <span className="ms-3 text-muted">{formatDate(sale.sale_date)}</span>
                                            {sale.status === 'paid' && (
                                                <Badge bg="success" className="ms-2">Paid</Badge>
                                            )}
                                        </div>
                                        <div>
                                            { sale.status !== 'paid' && (
                                                <Button
                                                    variant="primary"
                                                    size="sm"
                                                    onClick={() => handleEditClick(sale)}
                                                    className="me-2"
                                                >
                                                    <FaEdit /> Edit
                                                </Button>
                                            )}
                                            {canEditDelete() && sale.status !== 'paid' && (!sale.payments || sale.payments.length === 0) && (
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDeleteClick(sale)}
                                                >
                                                    <FaTrash /> Delete
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="p-3">
                                        <div className="table-responsive">
                                            <Table bordered hover size="sm" className="mb-0">
                                                <thead className="table-light">
                                                    <tr>
                                                        <th>Product</th>
                                                        <th className="text-center" style={{ width: '120px' }}>Quantity</th>
                                                        <th className="text-end">Total</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sale.sale_summary && sale.sale_summary.map((item, idx) => (
                                                        <tr key={idx}>
                                                            <td>{item.product_name}</td>
                                                            <td className="text-center">{item.quantity}</td>
                                                            <td className="text-end fw-bold">{item.total_amount}</td>
                                                        </tr>
                                                    ))}
                                                    <tr className="table-active">
                                                        <td colSpan="2" className="text-end fw-bold">Total:</td>
                                                        <td className="text-end fw-bold text-primary">
                                                            {sale.sale_summary?.reduce((sum, item) => sum + item.total_amount, 0) || 0}
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </Table>
                                        </div>
                                        {sale.notes && (
                                            <div className="mt-2 text-muted small">
                                                <strong>Notes:</strong> {sale.notes}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {/* Add Sale Modal */}
            <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="xl" centered>
                <Modal.Header closeButton className="bg-light p-2">
                    <Modal.Title><FaPlus className="me-2" />Add New Sale</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row className="mb-3">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>Sale Date</Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={newSaleData.sale_date}
                                        onChange={(e) => setNewSaleData({ ...newSaleData, sale_date: e.target.value })}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <div className="mb-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <Form.Label className="mb-0">Products</Form.Label>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleAddProductRow}
                                    disabled={loadingProducts}
                                >
                                    <FaPlus size={12} /> Add Product
                                </Button>
                            </div>

                            <div className="table-responsive">
                                <Table bordered hover size="sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Product</th>
                                            <th style={{ width: '120px' }}>Quantity</th>
                                            <th style={{ width: '120px' }}>Price</th>
                                            <th style={{ width: '100px' }}>Discount</th>
                                            <th style={{ width: '100px' }}>Extra</th>
                                            <th style={{ width: '100px' }}>Total</th>
                                            <th style={{ width: '50px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {newSaleData.products.length === 0 ? (
                                            <tr>
                                                <td colSpan="7" className="text-center text-muted py-4">
                                                    No products added. Click "Add Product" to add items.
                                                </td>
                                            </tr>
                                        ) : (
                                            newSaleData.products.map((product) => {
                                                const availableStock = product.product_id ? getAvailableStock(product.product_id) : 0;
                                                const filteredProducts = getFilteredProducts(product.temp_id, false, null);
                                                
                                                return (
                                                    <tr key={product.temp_id}>
                                                        <td>
                                                            <Select
                                                                options={filteredProducts.map(prod => ({
                                                                    value: prod.product_id,
                                                                    label: `${prod.product_name} (Stock: ${prod.stock_qty})`
                                                                }))}
                                                                value={product.product_id ? {
                                                                    value: product.product_id,
                                                                    label: product.name
                                                                } : null}
                                                                onChange={(option) => handleProductChange(product.temp_id, 'product_id', option?.value)}
                                                                placeholder="Select product"
                                                                classNamePrefix="react-select"
                                                            />
                                                        </td>
                                                        <td>
                                                            <Form.Control
                                                                type="number"
                                                                min="1"
                                                                max={availableStock}
                                                                value={product.qty ?? ''}
                                                                placeholder="Enter Qty"
                                                                onChange={(e) => handleProductChange(product.temp_id, 'qty', parseInt(e.target.value))}
                                                                isInvalid={product.qty > availableStock}
                                                            />
                                                            <small className="text-muted d-block mt-1">
                                                                Stock: {availableStock}
                                                            </small>
                                                            {product.product_id != null &&
                                                                product.product_id !== '' &&
                                                                product.qty &&
                                                                product.qty > availableStock && (
                                                                    <small className="text-danger">
                                                                        Exceeds available stock!
                                                                    </small>
                                                                )}
                                                        </td>
                                                        <td>
                                                            <Form.Control
                                                                type="number"
                                                                step="0.01"
                                                                value={product.price ?? ''}
                                                                placeholder="Enter price"
                                                                onChange={(e) => handleProductChange(product.temp_id, 'price', parseFloat(e.target.value))}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Form.Control
                                                                type="number"
                                                                step="0.01"
                                                                placeholder="Discount"
                                                                value={product.discount_price || ''}
                                                                onChange={(e) => handleProductChange(product.temp_id, 'discount_price', parseFloat(e.target.value))}
                                                            />
                                                        </td>
                                                        <td>
                                                            <Form.Control
                                                                type="number"
                                                                placeholder="Extra"
                                                                value={product.extra_price || ''}
                                                                onChange={(e) => handleProductChange(product.temp_id, 'extra_price', parseFloat(e.target.value))}
                                                            />
                                                        </td>
                                                        <td className="fw-bold">
                                                            {product.total_amount}
                                                        </td>
                                                        <td className="text-center">
                                                            <Button
                                                                variant="link"
                                                                className="text-danger p-0"
                                                                onClick={() => handleRemoveProductRow(product.temp_id)}
                                                            >
                                                                <FaTimes />
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                    {newSaleData.products.length > 0 && (
                                        <tfoot>
                                            <tr className="table-active">
                                                <td colSpan="5" className="text-end fw-bold">Grand Total:</td>
                                                <td className="fw-bold text-primary">
                                                    {calculateNewGrandTotal()}
                                                </td>
                                                <td></td>
                                            </tr>
                                        </tfoot>
                                    )}
                                </Table>
                            </div>
                        </div>

                        <Form.Group>
                            <Form.Label>Notes</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={newSaleData.notes}
                                onChange={(e) => setNewSaleData({ ...newSaleData, notes: e.target.value })}
                                placeholder="Additional notes..."
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer className="bg-light p-2">
                    <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCreateSale} disabled={submitting || newSaleData.products.length === 0}>
                        {submitting ? <Spinner animation="border" size="sm" className="me-2" /> : <FaCheck className="me-2" />}
                        Create Sale
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteConfirm} onHide={() => { setShowDeleteConfirm(false); setDeletingSale(null); }} centered>
                <Modal.Header closeButton>
                    <Modal.Title><FaTrash className="me-2 text-danger" />Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete sale <strong>{deletingSale?.sale_number}</strong>?</p>
                    <p className="text-danger mb-0"><small>This action cannot be undone.</small></p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowDeleteConfirm(false); setDeletingSale(null); }} disabled={submitting}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={submitting}>
                        {submitting ? <Spinner animation="border" size="sm" /> : 'Delete Sale'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}